/**
 * Open the management-contract flow for one of the owner's own properties
 * (COL-528).
 *
 * Both portal entry points — the sidebar's "Verwaltungsvertrag abschließen"
 * card and the pending property card's "Vertrag jetzt abschließen" — go
 * through here, so they cannot drift into two different ideas of what signing
 * means.
 *
 * WHY IT NAVIGATES rather than opening a dialog of its own. The signing flow
 * is a legal instrument: five steps, a Vollmacht with a drawn signature, and a
 * snapshot of the terms taken at signing time (src/pages/Onboarding.tsx). A
 * second, shorter path to a signed contract would be one that skips a
 * document, and the two would drift the first time either was corrected. The
 * portal's job is to get the owner to that flow; `owner_start_contract` mints
 * or resumes the token it runs on.
 *
 * Resuming is the server's doing: a pending token for the property is handed
 * back rather than a second one minted, so closing the flow and coming back
 * returns to the same contract.
 */
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";

/** Why the flow could not be opened, in terms the page can act on. */
export type StartContractError =
  | "not_your_property"
  | "not_awaiting_contract"
  | "no_email_on_account"
  | "unknown";

function classify(message: string): StartContractError {
  if (message.includes("not_your_property")) return "not_your_property";
  if (message.includes("not_awaiting_contract")) return "not_awaiting_contract";
  if (message.includes("no_email_on_account")) return "no_email_on_account";
  return "unknown";
}

export function useStartOwnerContract() {
  const navigate = useNavigate();
  return useMutation<string, StartContractError, number>({
    mutationFn: async (propertyId: number): Promise<string> => {
      const { data, error } = await (
        supabase as unknown as {
          rpc: (
            fn: string,
            args: Record<string, unknown>
          ) => Promise<{ data: unknown; error: { message?: string } | null }>;
        }
      ).rpc("owner_start_contract", { _property_id: propertyId });
      if (error) throw classify(error.message ?? "");
      if (!data) throw "unknown" as StartContractError;
      return String(data);
    },
    onSuccess: (token) => {
      navigate(`/onboarding/${token}`);
    },
  });
}
